<template>
  <el-dialog
    v-model="dialogVisible"
    title="上传头像"
    width="600px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
  >
    <div class="avatar-upload-dialog">
      <div class="dialog-tips">
        <p class="tips-text">该场考试的证书需要使用用户头像，请先上传头像</p>
      </div>

      <div class="avatar-section">
        <el-upload
          class="avatar-uploader"
          :show-file-list="false"
          :before-upload="beforeAvatarUpload"
          :http-request="handleAvatarUpload"
          accept="image/jpeg,image/png"
        >
          <div class="avatar-upload-area">
            <img
              v-if="avatarUrl"
              :src="avatarUrl"
              class="avatar-preview"
              alt="头像"
            />
            <div v-else class="avatar-placeholder">
              <el-icon class="avatar-icon"><Plus /></el-icon>
              <div class="upload-text">点击上传头像</div>
            </div>
            <!-- 删除按钮 -->
            <div
              v-if="avatarUrl"
              class="delete-avatar-btn"
              @click.stop="deleteAvatar"
            >
              <el-icon><Close /></el-icon>
            </div>
            <!-- 旋转控制按钮 -->
            <div v-if="avatarUrl" class="rotate-controls">
              <el-button
                circle
                size="small"
                @click.stop="rotateImage(-90)"
                title="逆时针旋转90°"
              >
                ↺
              </el-button>
              <el-button
                circle
                size="small"
                @click.stop="rotateImage(90)"
                title="顺时针旋转90°"
              >
                ↻
              </el-button>
              <el-button
                circle
                size="small"
                @click.stop="rotateImage(180)"
                title="旋转180°"
              >
                ⟲
              </el-button>
            </div>
          </div>
        </el-upload>

        <div class="upload-tips">
          <p class="tips-text">剪切时请将图片填充到框内,四周不要留空</p>
          <p class="warning-text">请勿使用二次拍摄的照片<br />建议现场拍摄</p>
        </div>

        <div class="upload-instructions">
          <p>1. 请现场使用摄像头拍照（优先）或者上传近期证件照片。</p>
          <p>2. 此处上传本人的照片，请【不要上传无关照片】，将用于颁发证书</p>
          <p>
            3.
            【不要使用若干年前的老照片片或者艺术照】等。也【不要使用手机去拍摄身份证等证件上面的照片】
          </p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="loading">
          保存
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { Plus, Close } from "@element-plus/icons-vue";
import { getFileFullUrl } from "@/utils";
import { postWithFile, put } from "@/request.js";
import {
  fixImageOrientation,
  rotateImage as rotateImageUtil,
  fileToDataURL,
  blobToFile,
} from "@/utils/imageUtils";

export default {
  name: "AvatarUploadDialog",
  components: {
    Plus,
    Close,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "success"],
  data() {
    return {
      dialogVisible: false,
      avatarUrl: "",
      avatarPath: "",
      loading: false,
      userInfo: null, // 存储用户完整信息
      currentFile: null, // 当前处理的文件对象
      rotationAngle: 0, // 当前旋转角度
    };
  },
  watch: {
    modelValue(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.getUserInfo();
      } else {
        // 关闭时重置状态
        this.currentFile = null;
        this.rotationAngle = 0;
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit("update:modelValue", false);
      }
    },
  },
  methods: {
    // 获取用户信息
    async getUserInfo() {
      try {
        const res = await this.$https.get("/api/my/home/getInfo");
        if (res.success && res.data) {
          // 保存完整的用户信息
          this.userInfo = res.data;
          // 如果idCardType为空，设置默认值为身份证
          if (!this.userInfo.idCardType) {
            this.userInfo.idCardType = "ID_CARD";
          }
          const { avatar } = res.data;
          if (avatar) {
            this.avatarUrl = getFileFullUrl(avatar);
            this.avatarPath = avatar;
          } else {
            this.avatarUrl = "";
            this.avatarPath = "";
          }
        }
      } catch (error) {
        console.error("获取用户信息失败:", error);
      }
    },
    // 上传前验证
    beforeAvatarUpload(file) {
      const isJPG =
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/png";
      const isLt3M = file.size / 1024 / 1024 < 3;

      if (!isJPG) {
        this.$message.error("图片只能是 jpg、jpeg 或 png 格式!");
        return false;
      }
      if (!isLt3M) {
        this.$message.error("图片大小不能超过 3MB!");
        return false;
      }
      return true;
    },
    // 自定义上传
    async handleAvatarUpload(options) {
      try {
        // 先修正EXIF方向
        let correctedFile = await fixImageOrientation(options.file);
        this.currentFile = correctedFile;
        this.rotationAngle = 0; // 重置旋转角度

        // 预览图片
        this.avatarUrl = await fileToDataURL(correctedFile);

        // 上传到服务器
        const formData = new FormData();
        formData.append("file", correctedFile);
        const res = await postWithFile("/api/upload", formData);
        if (res.success && res?.data) {
          this.avatarPath = res.data.path;
          this.$message.success("头像上传成功，您可以旋转调整方向");
        } else {
          this.$message.error("头像上传失败");
        }
      } catch (error) {
        console.error("上传失败:", error);
        this.$message.error("头像上传失败");
      }
    },
    // 旋转图片
    async rotateImage(degrees) {
      if (!this.currentFile) {
        this.$message.warning("请先上传图片");
        return;
      }

      try {
        const prevPath = this.avatarPath;
        const prevFile = this.currentFile;
        const prevRotationAngle = this.rotationAngle;

        // 更新旋转角度（用于预览显示）
        this.rotationAngle = (this.rotationAngle + degrees) % 360;

        // 基于当前文件旋转指定角度（增量旋转）
        const rotatedBlob = await rotateImageUtil(this.currentFile, degrees);

        // 更新预览（使用CSS transform显示旋转效果）
        const previewUrl = await fileToDataURL(rotatedBlob);
        this.avatarUrl = previewUrl;

        // 更新当前文件（用于下次旋转）
        this.currentFile = blobToFile(rotatedBlob, this.currentFile.name);

        // 重新上传旋转后的图片
        const formData = new FormData();
        formData.append("file", this.currentFile);
        const res = await postWithFile("/api/upload", formData);
        if (res.success && res?.data) {
          const uploadedPath = res.data.path;
          this.avatarPath = uploadedPath;
          await this.persistAvatarPathImmediately(uploadedPath);
          this.$message.success("图片已旋转并上传");
        } else {
          this.avatarPath = prevPath;
          this.avatarUrl = prevPath ? getFileFullUrl(prevPath) : "";
          this.currentFile = prevFile;
          this.rotationAngle = prevRotationAngle;
          this.$message.error("旋转后上传失败");
        }
      } catch (error) {
        console.error("旋转失败:", error);
        this.avatarUrl = this.avatarPath ? getFileFullUrl(this.avatarPath) : "";
        this.$message.error(error?.message || "图片旋转失败");
      }
    },
    async persistAvatarPathImmediately(path) {
      try {
        const infoRes = await this.$https.get("/api/my/home/getInfo");
        if (!infoRes?.success || !infoRes?.data) {
          throw new Error("获取用户信息失败，无法保存头像");
        }
        this.userInfo = infoRes.data;
        if (!this.userInfo.idCardType) {
          this.userInfo.idCardType = "ID_CARD";
        }
        const params = {
          ...this.userInfo,
          avatar: path,
        };
        const saveRes = await put("/api/my/home/updateInfo-simple", params);
        if (!saveRes?.success) {
          const errorMsg = this.translateErrorMessage(saveRes);
          throw new Error(errorMsg || "头像保存失败");
        }
      } catch (error) {
        const rollbackPath = this.userInfo?.avatar || "";
        this.avatarPath = rollbackPath;
        this.avatarUrl = rollbackPath ? getFileFullUrl(rollbackPath) : "";
        throw new Error(error?.message || "头像保存失败，请稍后重试");
      }
    },
    // 删除头像
    deleteAvatar() {
      this.avatarUrl = "";
      this.avatarPath = "";
      this.currentFile = null;
      this.rotationAngle = 0;
      this.$message.info("头像已删除");
    },
    // 验证必需字段
    validateRequiredFields(userInfo) {
      const requiredFields = [
        { field: "name", label: "姓名" },
        { field: "email", label: "邮箱" },
        { field: "mobile", label: "手机号" },
        { field: "idCardType", label: "证件类型" },
        { field: "idCard", label: "身份证号" },
        { field: "gender", label: "性别" },
      ];

      const missingFields = [];
      for (const { field, label } of requiredFields) {
        if (
          !userInfo[field] ||
          userInfo[field] === null ||
          userInfo[field] === undefined
        ) {
          missingFields.push(label);
        }
      }

      if (missingFields.length > 0) {
        return {
          valid: false,
          message: `请先补充完整信息：${missingFields.join("、")}`,
        };
      }

      return { valid: true };
    },
    // 将错误信息转换为中文
    translateErrorMessage(error) {
      if (!error) {
        return "保存失败，请稍后重试";
      }

      // 处理响应错误
      if (error.data) {
        const errorData = error.data;

        // 如果是表单验证错误
        if (errorData.data && typeof errorData.data === "object") {
          const fieldErrors = Object.values(errorData.data).filter(
            (msg) => msg
          );
          if (fieldErrors.length > 0) {
            return fieldErrors.join("；");
          }
        }

        // 如果是JSON解析错误
        if (errorData.debug) {
          const debugMsg = errorData.debug;
          if (debugMsg.includes("idCardType")) {
            return "证件类型不能为空，请先补充完整信息";
          }
          if (debugMsg.includes("MissingKotlinParameterException")) {
            // 提取缺失的字段名
            const fieldMatch = debugMsg.match(
              /parameter (\w+) which is a non-nullable type/
            );
            if (fieldMatch) {
              const fieldName = fieldMatch[1];
              const fieldMap = {
                idCardType: "证件类型",
                name: "姓名",
                email: "邮箱",
                mobile: "手机号",
                idCard: "身份证号",
                gender: "性别",
              };
              const fieldLabel = fieldMap[fieldName] || fieldName;
              return `${fieldLabel}不能为空，请先补充完整信息`;
            }
            return "信息不完整，请先补充完整信息";
          }
          // 其他错误信息，尝试提取中文部分
          if (errorData.title) {
            return errorData.title;
          }
          return "保存失败，请检查信息是否完整";
        }

        // 如果有message字段
        if (errorData.message) {
          return errorData.message;
        }
      }

      return "保存失败，请稍后重试";
    },
    // 保存
    async handleSave() {
      if (!this.avatarPath) {
        this.$message.warning("请先上传头像");
        return;
      }

      this.loading = true;
      try {
        // 先获取最新的用户信息
        if (!this.userInfo) {
          const res = await this.$https.get("/api/my/home/getInfo");
          if (res.success && res.data) {
            this.userInfo = res.data;
            // 如果idCardType为空，设置默认值为身份证
            if (!this.userInfo.idCardType) {
              this.userInfo.idCardType = "ID_CARD";
            }
          } else {
            this.$message.error("获取用户信息失败");
            this.loading = false;
            return;
          }
        }

        // 验证必需字段
        const validation = this.validateRequiredFields(this.userInfo);
        if (!validation.valid) {
          this.$message.error(validation.message);
          this.loading = false;
          return;
        }

        // 组合所有用户数据，更新头像
        const params = {
          ...this.userInfo,
          avatar: this.avatarPath,
        };

        const res = await put("/api/my/home/updateInfo-simple", params);
        if (res.success) {
          this.$message.success("保存成功");
          this.$emit("success");
          this.dialogVisible = false;
        } else {
          const errorMsg = this.translateErrorMessage(res);
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error("保存失败:", error);
        const errorMsg = this.translateErrorMessage(error);
        this.$message.error(errorMsg);
      } finally {
        this.loading = false;
      }
    },
    // 取消
    handleCancel() {
      this.dialogVisible = false;
    },
  },
};
</script>

<style lang="less" scoped>
.avatar-upload-dialog {
  .dialog-tips {
    margin-bottom: 20px;
    text-align: center;

    .tips-text {
      color: #d34f4f;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .avatar-section {
    text-align: center;

    .avatar-uploader {
      display: inline-block;
    }

    .avatar-upload-area {
      width: 200px;
      height: 260px;
      margin: 0 auto 20px;
      border: 2px dashed #ddd;
      border-radius: 8px;
      position: relative;
      overflow: hidden;
      background: #fafafa;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        border-color: #409eff;
      }

      .avatar-preview {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .avatar-placeholder {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #8c939d;

        .avatar-icon {
          font-size: 48px;
          margin-bottom: 10px;
        }

        .upload-text {
          font-size: 14px;
        }
      }

      .delete-avatar-btn {
        position: absolute;
        top: 5px;
        right: 5px;
        width: 28px;
        height: 28px;
        background: #ff4757;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        z-index: 10;
        color: #fff;

        &:hover {
          background: rgba(255, 71, 87, 0.8);
          transform: scale(1.1);
        }

        &:active {
          transform: scale(0.95);
        }
      }

      .rotate-controls {
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 8px;
        z-index: 10;
        background: rgba(0, 0, 0, 0.6);
        padding: 8px;
        border-radius: 20px;

        .el-button {
          background: rgba(255, 255, 255, 0.9);
          border: none;
          color: #333;

          &:hover {
            background: rgba(255, 255, 255, 1);
          }
        }
      }
    }

    .upload-tips {
      margin-bottom: 20px;

      .tips-text {
        color: #666;
        font-size: 14px;
        margin-bottom: 8px;
      }

      .warning-text {
        color: #ff4757;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.6;
      }
    }

    .upload-instructions {
      text-align: left;
      font-size: 14px;
      color: #333;
      line-height: 1.8;
      background: #f5f7fa;
      padding: 15px;
      border-radius: 4px;

      p {
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style>
